import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { GetManageableDocumentEmployeesResponse } from "@performyard/service-contracts";

import { DocumentsData } from "../../../pages/Documents/schema";

import {
	deleteDocument,
	editDocument,
	getDocuments,
	getManageableDocumentEmployees,
	updateDocumentVirusScanStatus,
} from "../../services/defs/documents";

interface GetDocumentsError {
	response: {
		status: number;
		message: string;
	};
}

export const useDocuments = (userId) => {
	return useQuery<DocumentsData, GetDocumentsError>({
		queryKey: ["documents", userId],
		queryFn: () => getDocuments(userId),
		retry: 1,
		meta: {
			errorMessage: "There was an error getting the documents.",
		},
		enabled: !!userId,
	});
};

/**
 * Hook to fetch the active employees the current user can manage documents for.
 */
export const useManageableDocumentEmployees = (enabled: boolean) => {
	return useQuery<GetManageableDocumentEmployeesResponse>({
		queryKey: ["documents", "manageable-employees"],
		queryFn: getManageableDocumentEmployees,
		meta: {
			errorMessage: "There was an error getting the employees you can manage documents for.",
		},
		enabled,
	});
};

export const useDeleteDocument = () => {
	return useMutation({ mutationFn: deleteDocument });
};

export const useEditDocument = () => {
	return useMutation({ mutationFn: editDocument });
};

/**
 * Hook for toggling AI retrieval on a document.
 * Disables default error handling as errors are shown via the failure icon UI.
 */
export const useToggleAiRetrieval = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: editDocument,
		meta: { disableDefaultErrorHandling: true },
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["ai", "documents", "accessible"] });
		},
	});
};

/**
 * Hook to update the virus scan status of a document. Used when a document has it's
 * "pending" property set to true. Calls endpoint to check virus scanning tags, updates
 * the document, and returns a status of "clean", "unsafe", or "no-tag".
 */
export const useUpdateDocumentVirusScanStatus = () => {
	return useMutation({ mutationFn: updateDocumentVirusScanStatus });
};
